import { Alert, Button, Card, Form, Input, Space, Typography } from "antd";
import type { DeviceForm } from "@/entity/logistics/device";
import { useState } from "react";
import { useNavigate } from "react-router";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { DEVICE_TYPE, TRANSPORT_TYPE } from "@/meta/logistics/device";
import { useUserStore } from "@/views/stores";
import { Grid as ZhGrid } from "zhihao-ui-react";

export interface DeviceManagementFormPageProps {
    createDevice?: (payload: DeviceForm) => Promise<unknown>;
    banButton?: boolean;
}

/** 复用 Vue 设备新增接口。 */
export async function createDeviceManagement(payload: DeviceForm) {
    const response = await shippingRequestHttp.service.post("/device", payload);
    return response.data;
}

/** 物流管理-设备新增，先覆盖 Vue Add 分支的核心注册字段。 */
export function DeviceManagementFormPage({
    createDevice = createDeviceManagement,
    banButton
}: DeviceManagementFormPageProps) {
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState("");
    // 对齐旧 Vue userStore.banButton，同时允许宿主显式传入封禁状态。
    const storeBanButton = useUserStore(state => state.banButton);
    const isButtonBanned = banButton ?? storeBanButton;

    /** 统一补齐 Vue 默认设备类型和运输工具类型，避免页面字段被误改。 */
    const submitForm = async (values: Partial<DeviceForm>) => {
        const payload = {
            ...values,
            type: DEVICE_TYPE.CAMERA.code,
            transportType: TRANSPORT_TYPE.SHIP.code
        } as DeviceForm;
        try {
            await createDevice(payload);
            navigate(-1);
        } catch {
            setErrorMessage("设备创建失败");
        }
    };

    return (
        <section aria-label="device-management-form-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title level={4}>新增设备</Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Form
                    layout="vertical"
                    initialValues={{ type: DEVICE_TYPE.CAMERA.code, transportType: TRANSPORT_TYPE.SHIP.code }}
                    onFinish={values => void submitForm(values)}
                >
                    <Form.Item name="type" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="transportType" hidden>
                        <Input />
                    </Form.Item>
                    <ZhGrid cols={4} gap={[30, 2]}>
                        <Form.Item label="名称" name="name" rules={[{ required: true, message: "请输入设备名称" }]}>
                            <Input aria-label="名称" maxLength={40} placeholder="请输入设备名称" />
                        </Form.Item>
                        <Form.Item label="类型">
                            <Input disabled value={DEVICE_TYPE.CAMERA.intro || DEFAULT_VALUE} />
                        </Form.Item>
                        <Form.Item
                            label="序列号"
                            name="serialNo"
                            rules={[{ required: true, len: 9, message: "请输入9个字符的序列号" }]}
                        >
                            <Input aria-label="序列号" maxLength={9} placeholder="请输入序列号" />
                        </Form.Item>
                        <Form.Item
                            label="ICCID"
                            name="dataCard"
                            rules={[{ required: true, len: 20, message: "请输入20个字符的ICCID" }]}
                        >
                            <Input aria-label="ICCID" maxLength={20} placeholder="请输入ICCID" />
                        </Form.Item>
                        <Form.Item
                            label="设备令牌"
                            name="validateCode"
                            rules={[{ required: true, len: 8, message: "请输入8个字符的设备令牌" }]}
                        >
                            <Input aria-label="设备令牌" maxLength={8} placeholder="请输入设备令牌" />
                        </Form.Item>
                        <Form.Item label="关联船舶" name="masterId">
                            <Input placeholder="请输入船舶 ID" />
                        </Form.Item>
                        <Form.Item
                            dependencies={["masterId"]}
                            label="安装位置"
                            name="position"
                            rules={[
                                ({ getFieldValue }) => ({
                                    required: Boolean(getFieldValue("masterId")),
                                    message: "请输入安装位置"
                                })
                            ]}
                        >
                            <Input maxLength={20} placeholder="请输入安装位置" />
                        </Form.Item>
                        <Form.Item label="安装人" name="installerInfo">
                            <Input placeholder="请搜索安装人" />
                        </Form.Item>
                        <Form.Item label="安装日期" name="installDate">
                            <Input aria-label="安装日期" placeholder="请选择安装日期" type="date" />
                        </Form.Item>
                        <Form.Item label="安装地点" name="installSite">
                            <Input maxLength={32} placeholder="请输入安装地点" />
                        </Form.Item>
                        <Form.Item label="推介人" name="referrerId">
                            <Input aria-label="推介人" placeholder="请输入推介人账号ID" type="number" />
                        </Form.Item>
                    </ZhGrid>
                    <Space className="operation-fixed-btn">
                        <Button disabled={isButtonBanned} htmlType="submit" type="primary">
                            提交
                        </Button>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}
